// Game X (Phase 1) — self-contained guess matching. No imports, pure functions.
// Deleting this file + GameX.jsx removes the feature entirely.

// "A.J. Green" -> "ajgreen"; " Zydrunas  Ilgauskas " -> "zydrunasilgauskas"
export function normalizeName(s) {
  if (!s) return '';
  return s
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

// Damerau-Levenshtein (optimal string alignment): transpositions cost 1,
// so the classic "farve" -> "favre" slip counts as a single edit.
export function damerau(a, b) {
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  const d = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) d[i][0] = i;
  for (let j = 0; j <= n; j++) d[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(
        d[i - 1][j] + 1,
        d[i][j - 1] + 1,
        d[i - 1][j - 1] + cost
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[m][n];
}

// Longer targets earn more typo room; short ones must be exact.
function threshold(target) {
  const len = target.length;
  if (len <= 4) return 0;
  if (len <= 8) return 1;
  return 2;
}

// Guess wins against: the full name, the last-name token, or any alias -
// each normalized, each with a length-scaled typo allowance.
// Returns { correct, matchedOn } where matchedOn names the target that hit.
export function matchGuess(guess, answer, aliases = []) {
  const g = normalizeName(guess);
  // Sub-3-char guesses are noise EXCEPT an exact alias hit: initials like
  // "KD" are the canonical name for some guys, and burning a guess on them
  // is brutal. Exact-only below the floor, so typo tolerance never applies.
  if (g.length < 3) {
    for (const a of aliases) {
      if (normalizeName(a) === g) return { correct: true, matchedOn: `alias "${a}"` };
    }
    return { correct: false, matchedOn: null };
  }

  const parts = (answer || '').trim().split(/\s+/);
  const lastToken = parts.length > 1 ? parts[parts.length - 1] : null;

  const targets = [
    { label: 'full name', raw: answer },
    ...(lastToken ? [{ label: 'last name', raw: lastToken }] : []),
  ];
  for (const a of aliases) {
    targets.push({ label: `alias "${a}"`, raw: a });
    // "The Gunslinger" should also accept plain "gunslinger"
    const deArticled = a.replace(/^\s*the\s+/i, '');
    if (deArticled !== a) targets.push({ label: `alias "${a}"`, raw: deArticled });
  }

  for (const t of targets) {
    const norm = normalizeName(t.raw);
    if (!norm) continue;
    if (damerau(g, norm) <= threshold(norm)) {
      return { correct: true, matchedOn: t.label };
    }
  }
  return { correct: false, matchedOn: null };
}

// Autocomplete assist: min 3 typed chars, startsWith hits ranked before
// contains hits, capped. Purely a spelling aid - raw text always submittable.
export function suggest(query, names, cap = 8) {
  const q = normalizeName(query);
  if (q.length < 3) return [];
  const starts = [];
  const contains = [];
  for (const name of names) {
    const n = normalizeName(name);
    if (n.startsWith(q)) starts.push(name);
    else if (n.includes(q)) contains.push(name);
    if (starts.length >= cap) break;
  }
  return starts.concat(contains).slice(0, cap);
}
